import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap, tap, withLatestFrom, catchError, debounceTime } from 'rxjs/operators';
import { concat, of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { differenceInYears, parseISO } from 'date-fns';
import { APIService } from '../../services/api/api.service';
import * as CustomerActions from './customer.actions';
import * as CustomerSelectors from './customer.selectors';
import { DrawerType, ICustomerCreditType } from '@care-portals/api-interfaces';
import { AppointmentFacade } from '../appointment/appointment.facade';
import { DrawerStateFacade } from '../drawer-state/drawer-state.facade';
import { withOrgNavigationExtras } from '../app/layout-org.util';
import { DrawerService } from '../../services/drawer/drawer.service';
import { Lead } from '@appgen-health-crm/models';
import { ListQueryService } from '../../components/common/pagination/list-query.service';
import { listInvalidationEffect } from '../../components/common/pagination/list-query-invalidation.effect';
import { CUSTOMER_LIST_QUERY_PREFIX } from './customer.query-keys';

@Injectable()
export class CustomerEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly apiService: APIService,
    private readonly notification: NzNotificationService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly AppointmentFacade: AppointmentFacade,
    private readonly drawerService: DrawerService,
    private readonly listQuery: ListQueryService
  ) {}

  // A customer mutation can change what the list page shows, so drop the keyed
  // list caches (the offset/SWR path the list page renders from). The embedded
  // NgRx-backed views are unaffected.
  invalidateListQueries$ = listInvalidationEffect(
    () => this.actions$,
    () => this.listQuery,
    CUSTOMER_LIST_QUERY_PREFIX,
    [
      CustomerActions.createCustomerSuccess,
      CustomerActions.updateCustomerSuccess,
      CustomerActions.updateCustomerExtrasSuccess,
      CustomerActions.updateCustomerCreditSuccess
    ]
  );

  loadCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadCustomers),
      switchMap(action => of(action).pipe(withLatestFrom(this.store.select(CustomerSelectors.getCustomerState)))),
      debounceTime(500),
      switchMap(([action, state]) => {
          const filters = { ...action.filters };
          if (action.loadType === 'newer' && state.maxId) {
            Object.assign(filters, { minId: state.maxId });
          } else if (action.loadType === 'older' && state.minId) {
            Object.assign(filters, { maxId: state.minId });
          }

          return this.apiService.getCustomers(filters).pipe(
            map(res => (Array.isArray(res) ? res : res.data)),
            tap(customers => {
              if (!customers.length) {
                if (action.loadType === 'newer') {
                  this.notification.info('Info', 'You are all caught up!');
                } else {
                  this.notification.info(
                    'Info',
                    'There are no older customers'
                  );
                }
              }
            }),
            map(customers => {
              return customers.map(customer => {
                return {
                  ...customer,
                  age:
                    differenceInYears(new Date(), parseISO(customer.dob)) || 0
                };
              });
            }),
            map(customers =>
              CustomerActions.loadCustomersSuccess({
                customers,
                loadType: action.loadType
              })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error loading customers; Try Again!');
              return of(CustomerActions.loadCustomersFailure({ error }));
            })
          );
      })
    )
  );

  loadCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadCustomer),
      switchMap(action =>
        this.apiService.getCustomer(action.id).pipe(
          map(customer => {
            if (!customer) {
              return CustomerActions.loadCustomerFailure({ error: 'Patient not found' });
            }
            return CustomerActions.loadCustomerSuccess({ customer });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error getting customer.');
            return of(CustomerActions.loadCustomerFailure({ error }));
          })
        )
      )
    )
  );

  loadCustomerLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadCustomerLead),
      switchMap(action =>
        this.apiService.getCustomerLead(action.id).pipe(
          map(lead => {
            if (!lead) {
              return CustomerActions.loadCustomerLeadFailure({ error: 'Patient not found' });
            }
            return CustomerActions.loadCustomerLeadSuccess({ lead });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error getting customer lead.');
            return of(CustomerActions.loadCustomerLeadFailure({ error }));
          })
        )
      )
    )
  );

  createCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.createCustomer),
      switchMap(action =>
        this.apiService
          .createCustomer(action.customer, action.lead)
          .pipe(
            tap(customer => {
              this.notification.success('Success', 'Client Account Created');
              if (action.goToCustomer) {
                this.router.navigate(
                  ['/customers', customer._id],
                  withOrgNavigationExtras({ onSameUrlNavigation: 'reload' })
                );
              }
              this.drawerService.closeDrawer(DrawerType.CREATE_PATIENT);
            }),
            map(customer => CustomerActions.createCustomerSuccess({ customer })),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error creating customer.');
              return of(CustomerActions.createCustomerFailure({ error }));
            })
          )
      )
    )
  );

  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateCustomer),
      withLatestFrom(this.store.select(CustomerSelectors.selectCurrentCustomerId)),
      switchMap(([action, customerId]) => {
        if (!customerId || action.id !== customerId) {
          return of(CustomerActions.updateCustomerFailure({ error: 'Selected customer does not matching updates' as any }));
        }
        return this.apiService.updateCustomer(action.id, action.data).pipe(
          map(customer => {
            const changes = {
              ...customer,
              ...(action.data.pickupPharmacy === null
                ? { pickupPharmacy: null }
                : {}),
              ...(action.data.pickupPharmacyId === null
                ? { pickupPharmacyId: null }
                : {})
            };
            return CustomerActions.updateCustomerSuccess({
              customer: { changes, id: action.id }
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error updating customer.');
            return of(CustomerActions.updateCustomerFailure({ error }));
          })
        );
      })
    )
  );

  updateCustomerExtras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateCustomerExtras),
      switchMap(action =>
        this.apiService.updateCustomerExtras(action.id, action.extras).pipe(
          map(customer => {
            this.notification.success('Extras updated successfully', '');
            return CustomerActions.updateCustomerExtrasSuccess({
              customer: { changes: customer, id: action.id }
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating customer extras.'
            );
            return of(CustomerActions.updateCustomerExtrasFailure({ error }));
          })
        )
      )
    )
  );

  updateCustomerSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomerActions.updateCustomerSuccess),
        tap(() => {
          this.notification.success('Updated Successfully', '');
        })
      ),
    { dispatch: false }
  );

  updateWatchlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateWatchlist),
      withLatestFrom(this.store.select(CustomerSelectors.selectCurrentCustomer)),
      switchMap(([action, currentCustomer]) =>
        this.apiService
          .updateWatchlist(currentCustomer._id, action.isAdd, action.userId)
          .pipe(
            map(watchlist => {
              const updates = { id: currentCustomer._id, changes: { ...currentCustomer, watchlist } };
              return CustomerActions.updateCustomerSuccess({ customer: updates });
            }),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error in adding watchlist.');
              return of(CustomerActions.updateWatchlistFailure({ error }));
            })
          )
      )
    )
  );

  openCustomerByBigcommerceId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.openCustomerByBigcommerceId),
      switchMap(action =>
        this.apiService
          .getCustomers({ bigcommerceId: action.bigcommerceId })
          .pipe(
            map(res => (Array.isArray(res) ? res : res.data)),
            map(customers => {
              if (customers?.length === 1) {
                return customers[0];
              } else if (customers?.length > 1) {
                this.notification.warning('Warning', 'Customer duplicated.');
              } else {
                this.notification.warning('Warning', 'Customer not found.');
              }
              return null;
            }),
            map(customer => (customer ? CustomerActions.openCustomerByBigcommerceIdSuccess({ customer }) : CustomerActions.nullAction())),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error to go to customer page.');
              return of(CustomerActions.nullAction());
            })
          )
      )
    )
  );

  openCustomerByBigcommerceIdSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomerActions.openCustomerByBigcommerceIdSuccess),
        tap(action =>
          this.router.navigate(
            ['/customers', action.customer._id],
            withOrgNavigationExtras()
          )
        )
      ),
    { dispatch: false }
  );

  loadCustomerActivities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadCustomerActivities),
      switchMap(({ customerId }) =>
        this.apiService.emr.activities
          .list(customerId)
          .then(res => {
            return CustomerActions.loadCustomerActivitiesSuccess({
              customerId,
              activities: res.data,
              hasMore: res.hasMore
            });
          })
          .catch(error => {
            return CustomerActions.loadCustomerActivitiesFailure({
              customerId,
              error
            });
          })
      )
    )
  );

  loadCustomerPaymentMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadPaymentMethods),
      switchMap(({ id }) =>
        this.apiService.getCustomerPaymentMethods(id).pipe(
          map(paymentMethods => CustomerActions.loadPaymentMethodsSuccess({ customerId: id, paymentMethods })),
          catchError(error => of(CustomerActions.loadPaymentMethodsFailure({ customerId: id, error })))
        )
      )
    )
  );

  /* ------------------------------------------------------------------------ */
  /*                       Customer Appointments Effects                      */
  /* ------------------------------------------------------------------------ */

  loadCustomerAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.loadAppointments),
      withLatestFrom(this.store.select(CustomerSelectors.selectCurrentCustomerId)),
      switchMap(([action, customer]) =>
        this.apiService
          .getAppointments({ ...action.filters, leadId: customer })
          .pipe(
            map(appointments => CustomerActions.loadAppointmentsSuccess({ appointments })),
            catchError(error => of(CustomerActions.loadAppointmentsFailure({ error })))
          )
      )
    )
  );

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateAppointment),
      switchMap(action =>
        this.apiService
          .updateAppointment(action.id, action.appointment)
          .pipe(
            tap(() => this.notification.success('Successfully updated.', '')),
            map(appointment => CustomerActions.updateAppointmentSuccess({ appointment })),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error updating appointment.');
              return of(CustomerActions.updateAppointmentFailure({ error }));
            })
          )
      )
    )
  );

  createActivitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomerActions.createActivitySuccess),
        tap(() =>
          this.notification.success('Activity Logged Successfully.', '')
        )
      ),
    { dispatch: false }
  );

  createActivityFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomerActions.createActivityFailure),
        tap(error =>
          console.error('[CustomerEffects] createActivityFailure', error)
        ),
        tap(error =>
          this.notification.error(
            'Error',
            'There was an error creating activity.'
          )
        )
      ),
    { dispatch: false }
  );

  updateCustomerCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateCustomerCredit),
      switchMap(action =>
        this.apiService
          .updateCustomerCredit(action.customerId, action.amount, action.note, action.orderId)
          .pipe(
            tap(() => this.notification.success('Success', 'Credits updated successfully')),
            map(customer => CustomerActions.updateCustomerCreditSuccess({ customerId: action.customerId, credit: customer.credit })),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error updating customer credits.');
              return of(CustomerActions.updateCustomerCreditFailure({ error }));
            })
          )
      )
    )
  );
}
